/**
 * Per-claim detail page — route /claims/:id (P3b: released evidence only).
 *
 * ``id`` = the candidate ``claim_id`` or its feed uuid (GET /api/evi-claims/{id}, released feed
 * ``app/services/evi_feed.py``). Shows the Variant-A card (SVG inline + PNG), the article chart
 * (SVG + PNG), the design/source line and the chart data; the full proof (omitted rows, exact
 * p-values, study figures) lives on the Evidence board. Old archive ids (evi_consumer_claim) and
 * withdrawn cards resolve to "not released". English-only (Atlas).
 */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AlertTriangle, ArrowUpRight, Download, FileText, Loader2 } from "lucide-react";
import { api, apiUrl, ApiError } from "../api";
import { EmptyState, useToast } from "../ui";
import { BlogBackLink } from "./blog/shared";
import { InlineSvg } from "./EvidenceReviewTab";
import { CLASS_LABEL, type ReleasedClaim } from "./releasedClaims";

interface ChartSpec {
  categories?: string[];
  series?: { name: string; values?: (number | null)[]; highlight?: boolean; sig?: (string | null)[] }[];
  yName?: string | null;
}
interface ClaimDetail extends ReleasedClaim {
  chart_spec: ChartSpec | null;
}

export default function ClaimDetailPage() {
  const { id = "" } = useParams();
  const toast = useToast();
  const [claim, setClaim] = useState<ClaimDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setNotFound(false);
      try {
        const d = await api.get<ClaimDetail>(`/api/evi-claims/${encodeURIComponent(id)}`);
        if (!cancelled) setClaim(d);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else if (!cancelled) toast.err("Failed to load claim", String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (loading && !claim) {
    return (
      <div className="mx-auto flex max-w-5xl items-center gap-2 p-6 text-text-3">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading claim …
      </div>
    );
  }
  if (notFound || !claim) {
    return (
      <div className="mx-auto max-w-5xl p-6">
        <BackLink />
        <div className="mt-6">
          <EmptyState
            icon={<FileText className="h-6 w-6" />}
            title="Claim not released"
            hint="This claim is not (or no longer) released. Released claims are listed under Claims; the Evidence board holds every candidate."
          />
        </div>
      </div>
    );
  }

  const cs = claim.chart_spec;
  const slug = claim.claim_id.replace(/[^a-z0-9]+/gi, "-");

  return (
    <div className="mx-auto max-w-5xl space-y-5 px-4 py-6 md:px-6" data-claim-detail={claim.claim_id}>
      <BackLink />

      <header className="space-y-2">
        <div className="flex flex-wrap items-center gap-2 text-[0.7rem] uppercase tracking-wider text-text-3">
          <span className="font-semibold text-accent-700 dark:text-accent-300">{claim.effect_area_en}</span>
          <span>· {CLASS_LABEL[claim.view_class]}</span>
          {claim.strength_tier && <span>· {claim.strength_tier}</span>}
          <span>· {claim.source_en}</span>
        </div>
        <h1 className="font-display text-2xl font-semibold leading-tight tracking-tight text-text">{claim.headline_en}</h1>
        <p className="text-sm text-text-2">{claim.design_line_en}</p>
        {claim.has_caveat && claim.caveat && (
          <div className="flex items-start gap-2 rounded-card border border-warning/30 bg-warning-soft/50 px-3 py-2 text-sm text-text-2">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            {claim.caveat}
          </div>
        )}
        <Link
          to={claim.evidence_url}
          className="inline-flex items-center gap-1 text-sm font-medium text-accent-700 hover:text-accent-600 dark:text-accent-300"
        >
          Proof on the Evidence board <ArrowUpRight className="h-4 w-4" />
        </Link>
      </header>

      <div className="grid gap-4 md:grid-cols-2">
        <section className="space-y-2 rounded-card border border-border bg-surface p-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-text-2">Card</h2>
          <InlineSvg url={claim.card_svg} label={claim.headline_en} />
          <Downloads svg={claim.card_svg} png={claim.card_png} name={`ectoin-${slug}-card`} />
        </section>
        {claim.chart_svg && claim.chart_png && (
          <section className="space-y-2 rounded-card border border-border bg-surface p-3">
            <h2 className="text-xs font-semibold uppercase tracking-wider text-text-2">Article chart</h2>
            <InlineSvg url={claim.chart_svg} label={claim.headline_en} />
            <Downloads svg={claim.chart_svg} png={claim.chart_png} name={`ectoin-${slug}-chart`} />
          </section>
        )}
      </div>

      {cs && cs.categories && cs.series && (
        <section className="space-y-2 rounded-card border border-border bg-surface p-4">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-text-2">
            Chart data{cs.yName ? ` · ${cs.yName}` : ""}
          </h2>
          <div className="overflow-x-auto">
            <table className="min-w-full border-collapse text-xs">
              <thead>
                <tr className="text-text-3">
                  <th className="pb-1 pr-3 text-left font-medium"></th>
                  {cs.categories.map((cat, i) => (
                    <th key={i} className="whitespace-nowrap px-2 pb-1 text-right font-medium">{cat}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {cs.series.map((ser, si) => (
                  <tr key={si} className={ser.highlight ? "font-semibold text-accent-700 dark:text-accent-300" : ""}>
                    <td className="whitespace-nowrap py-0.5 pr-3 text-left text-text">{ser.name}</td>
                    {(ser.values ?? []).map((val, vi) => (
                      <td key={vi} className="whitespace-nowrap px-2 py-0.5 text-right font-mono text-text">
                        {val ?? "—"}
                        {ser.sig && ser.sig[vi] ? <sup className="ml-0.5 text-accent-600">{ser.sig[vi]}</sup> : null}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </div>
  );
}

function Downloads({ svg, png, name }: { svg: string; png: string; name: string }) {
  const cls = "inline-flex items-center gap-1 text-xs text-accent-700 hover:text-accent-600 dark:text-accent-300";
  return (
    <div className="flex gap-3">
      <a href={apiUrl(svg)} download={`${name}.svg`} className={cls}>
        <Download className="h-3.5 w-3.5" /> SVG
      </a>
      <a href={apiUrl(png)} download={`${name}.png`} className={cls}>
        <Download className="h-3.5 w-3.5" /> PNG
      </a>
    </div>
  );
}

function BackLink() {
  return <BlogBackLink to="/blog/dashboard?tab=claims" label="Back to Claims" />;
}
